import {
  CheckDiscountCodeForm,
  CheckDiscountCodeSchema,
  CreateTransactionForm,
  useCheckDiscountCodeMutation,
  useNewTicketingOrderMutation,
} from "@ac-expo/api";
import {
  BasketErrorsConflict,
  CreateTransactionResponseData,
  EventDetails,
  EventFormQuestionOnEvent,
  ValidateTransactionResponse,
} from "@ac-expo/api-types";
import { BuyableEntryTypeWithKey, UseLocalCartReturns } from "@ac-expo/hooks";
import {
  Button,
  errorRingClassName,
  errorTextClassName,
  Form,
  FormCheckbox,
  FormInput,
  Input,
  Svg,
  toast,
} from "@ac-expo/ui-components";
import {
  CartRefusal,
  getOrderRefusals,
  GROUP_TICKET_EMAIL_LOCK_MESSAGE,
  hasGroupTicketInCart,
} from "@ac-expo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import BillingComponent from "@web-app/src/components/Cart/BillingComponent";
import CheckoutRefusals from "@web-app/src/components/Cart/CheckoutRefusals";
import PaymentMethods from "@web-app/src/components/Cart/PaymentMethods";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import { BuyerInformationsForm } from "@web-app/src/schema/embedTicketing.schema";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { EmbedNavigation } from "./EmbedNavigation";
import { EmbedGuestSession } from "./embed-guest-session";
import { STEPS } from "./EmbedTicketing";
import TotalCart from "./TotalCart";
import {
  applyEmbeddedDiscountCode,
  formatCartItemsForExport,
  getInformationsForConsent,
} from "./utils";

const ORDER_REDIRECT_KEY = "order_redirect";

type EmbedInformationsComponentProps = {
  setStep: (step: STEPS) => void;
  event: EventDetails;
  localCart: UseLocalCartReturns;
  buyableTicketTypes: BuyableEntryTypeWithKey<"ticket">[];
  buyableBadgeTypes: BuyableEntryTypeWithKey<"badge">[];
  buyableActivityTicketTypes: BuyableEntryTypeWithKey<"activity">[];
  transactionForm: UseFormReturn<BuyerInformationsForm>;
  setPaymentLink: Dispatch<
    SetStateAction<CreateTransactionResponseData | null>
  >;
  setTotal: (price: number) => void;
  setTotalDiscount: (price: number) => void;
  totalPrice: number;
  totalDiscount: number;
  setResult: Dispatch<SetStateAction<ValidateTransactionResponse | null>>;
  setIsTransactionComplete: Dispatch<SetStateAction<boolean>>;
  eventFormQuestionsOnEvent: EventFormQuestionOnEvent[] | undefined;
  setFormAnswersToken: Dispatch<SetStateAction<string | null>>;
  // Guest session opened by the Group unlock, null while the visitor is anonymous
  guestSession: EmbedGuestSession | null;
};

export function EmbedInformationsComponent({
  setStep,
  event,
  localCart,
  buyableTicketTypes,
  buyableBadgeTypes,
  buyableActivityTicketTypes,
  transactionForm,
  setPaymentLink,
  setTotal,
  setTotalDiscount,
  totalPrice,
  totalDiscount,
  setResult,
  setIsTransactionComplete,
  eventFormQuestionsOnEvent,
  setFormAnswersToken,
  guestSession,
}: EmbedInformationsComponentProps) {
  const router = useRouter();
  const { totalPricing, getTotalQuantity } = localCart;

  // Reasons the server refused the basket: the embed submits the order directly,
  // so they only reach this step through the conflict response.
  const [refusals, setRefusals] = useState<CartRefusal[]>([]);

  // A Group ticket is only eligible for the email that unlocked it (ADR 0005): the
  // order must be placed with that email while one is selected
  const hasGroupTicketSelected = useMemo(
    () =>
      hasGroupTicketInCart(
        buyableTicketTypes.map((entry) => ({
          quantity: getTotalQuantity(entry.itemKey),
          buyableItem: { ticketType: { userType: entry.entryType.userType } },
        }))
      ),
    [buyableTicketTypes, getTotalQuantity]
  );

  const emailLockMessage = hasGroupTicketSelected
    ? GROUP_TICKET_EMAIL_LOCK_MESSAGE
    : null;

  useEffect(() => {
    if (!guestSession) return;
    const currentEmail = transactionForm.getValues("email");

    if (!currentEmail || hasGroupTicketSelected) {
      transactionForm.setValue("email", guestSession.email);
    }
  }, [guestSession, hasGroupTicketSelected, transactionForm]);

  const { organiserId, organiserName, structureIds, structuresMapping } =
    useMemo(() => {
      const activityTicketsSelected = buyableActivityTicketTypes
        .map((entry) => ({
          id: entry.entryType.id,
          quantity: getTotalQuantity(entry.itemKey),
        }))
        .filter((item) => item.quantity > 0);
      return getInformationsForConsent(event, activityTicketsSelected);
    }, [event, buyableActivityTicketTypes, getTotalQuantity]);

  const discountCodes = transactionForm.watch("discountCodes");
  const isPaid = useMemo(() => totalPricing.total > 0, [totalPricing.total]);

  const discountCodeForm = useForm<CheckDiscountCodeForm>({
    resolver: zodResolver(CheckDiscountCodeSchema),
  });
  const discountCodeRegister = discountCodeForm.register("discountCode");

  const mutationCheckDiscountCode = useCheckDiscountCodeMutation();
  const mutationNewTicketingOrder = useNewTicketingOrderMutation();

  const ticketsForExport = useMemo(
    () =>
      formatCartItemsForExport(
        [...buyableTicketTypes, ...buyableActivityTicketTypes],
        getTotalQuantity
      ),
    [buyableTicketTypes, buyableActivityTicketTypes, getTotalQuantity]
  );

  const badgesForExport = useMemo(
    () => formatCartItemsForExport(buyableBadgeTypes, getTotalQuantity),
    [buyableBadgeTypes, getTotalQuantity]
  );

  const applyPromoCode = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const { discountCode } = discountCodeForm.getValues();

    if (!discountCode) {
      return;
    }

    await applyEmbeddedDiscountCode({
      mutation: mutationCheckDiscountCode,
      discountCode,
      discountCodes,
      ticketsForExport,
      badgesForExport,
      transactionForm,
      discountCodeForm,
      setTotal,
      setTotalDiscount,
    });
  };

  // The displayed total is only known server-side (partial/capped codes), so
  // removing a code re-prices the remaining ones instead of guessing locally.
  const removeCode = async (code: string) => {
    const remainingCodes = (discountCodes ?? []).filter((c) => c !== code);
    transactionForm.setValue("discountCodes", remainingCodes);

    if (remainingCodes.length === 0) {
      setTotal(totalPricing.total);
      setTotalDiscount(totalPricing.discount);
      return;
    }

    await applyEmbeddedDiscountCode({
      mutation: mutationCheckDiscountCode,
      discountCode: remainingCodes[remainingCodes.length - 1],
      discountCodes: remainingCodes.slice(0, -1),
      ticketsForExport,
      badgesForExport,
      transactionForm,
      setTotal,
      setTotalDiscount,
    });
  };

  const onSubmit = () => {
    const values = transactionForm.getValues();
    setRefusals([]);

    // A refused basket is an expected answer, reported by onError: it must not reject
    // the submission and surface as an unhandled rejection.
    mutationNewTicketingOrder.mutate(
      {
        ...values,
        tickets: ticketsForExport,
        badges: badgesForExport,
      },
      {
        onSuccess: (result) => {
          const transaction = result.transaction;
          // The discount-replacement branch returns no transaction (only a message)
          if (!transaction) {
            return;
          }
          if (result.formAnswersToken) {
            setFormAnswersToken(result.formAnswersToken);
          }

          if (!transaction.shouldPay) {
            setIsTransactionComplete(true);
            setResult({
              id: transaction.data.id,
              status: transaction.data.status,
              reference: transaction.data.reference,
            });

            const hasFormQuestions =
              eventFormQuestionsOnEvent && eventFormQuestionsOnEvent.length > 0;
            setStep(hasFormQuestions ? "FORM" : "CONFIRMATION");
            return;
          }

          localStorage.setItem(ORDER_REDIRECT_KEY, router.asPath);
          setPaymentLink(transaction.data);
          const hasFormQuestions =
            eventFormQuestionsOnEvent && eventFormQuestionsOnEvent.length > 0;
          setStep(hasFormQuestions ? "FORM" : "PAYMENT");
        },
        onError: (error) => {
          const orderRefusals = isAxiosError<BasketErrorsConflict>(error)
            ? getOrderRefusals(error.response?.data.basketErrors)
            : [];

          // Per-line refusals are shown here, attached to their ticket: no anonymous toast
          if (orderRefusals.length > 0) {
            setRefusals(orderRefusals);

            return;
          }

          toast({
            title: "Une erreur est survenue",
            description: error.message,
          });
        },
      }
    );
  };

  const handlePreviousStep = () => {
    setRefusals([]);
    setStep("CART");
    discountCodeForm.reset();
    setTotal(totalPricing.total);
    setTotalDiscount(totalPricing.discount);
    transactionForm.setValue("discountCodes", []);
  };

  return (
    <div className="flex flex-col">
      <Form {...transactionForm}>
        <form
          onSubmit={transactionForm.handleSubmit(onSubmit)}
          className="flex flex-col gap-2"
        >
          <CheckoutRefusals
            refusals={refusals}
            onBackToCart={handlePreviousStep}
          />

          {isPaid && (
            <>
              <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4">
                <p className="font-bold">Code promo</p>
                <div className="w-full max-w-sm">
                  <div className="flex items-center space-x-2">
                    <Input
                      id="apply-promo-code-input"
                      placeholder="Code"
                      className={
                        discountCodeForm.formState.errors.discountCode
                          ? errorRingClassName
                          : undefined
                      }
                      {...discountCodeRegister}
                      onChange={(e) => {
                        discountCodeRegister.onChange(e);
                        if (discountCodeForm.formState.errors.discountCode) {
                          discountCodeForm.clearErrors("discountCode");
                        }
                      }}
                    />
                    <Button onClick={applyPromoCode}>Valider</Button>
                  </div>
                  {discountCodeForm.formState.errors.discountCode?.message && (
                    <p className={`${errorTextClassName} mt-1`}>
                      {discountCodeForm.formState.errors.discountCode?.message}
                    </p>
                  )}
                </div>
                {discountCodes && discountCodes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {discountCodes.map((code) => (
                      <div
                        key={code}
                        className="inline-flex w-fit flex-wrap items-center whitespace-nowrap rounded-lg border py-[2px] pl-[9px] pr-1 text-sm"
                      >
                        Code#{code}
                        <button
                          type="button"
                          onClick={() => removeCode(code)}
                          className="ml-2"
                        >
                          <Svg name="close" alt="fermer" className="size-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex grow flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
                <PaymentMethods />
              </div>
            </>
          )}
          <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4">
            <p className="font-bold">Informations sur l&apos;acheteur</p>
            <FormInput
              control={transactionForm.control}
              name="email"
              label="Email"
              placeholder="Email"
              disabled={emailLockMessage !== null}
            />
            {emailLockMessage && (
              <p className="text-xs text-gray-500">{emailLockMessage}</p>
            )}
            <BillingComponent
              transactionForm={
                transactionForm as UseFormReturn<
                  CreateTransactionForm | BuyerInformationsForm
                >
              }
            />
            <FormCheckbox
              control={transactionForm.control}
              name="acceptedTerms"
              className="mt-2"
              checkboxClassNames={
                transactionForm.getFieldState("acceptedTerms").error
                  ? "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background rounded-[5px] ring-1 focus-visible:ring-offset-1"
                  : undefined
              }
              labelClassName="text-sm"
              labelContent={
                <p>
                  J&apos;ai lu et j&apos;accepte les{" "}
                  <a
                    href="/cgv"
                    className="text-blue-400 underline"
                    target="_blank"
                  >
                    conditions générales de vente
                  </a>{" "}
                  et j&apos;ai pris connaissance de la{" "}
                  <a
                    className="text-blue-400 underline"
                    href="/politique-de-confidentialite"
                    target="_blank"
                  >
                    politique de confidentialité
                  </a>{" "}
                  de Connect&apos;Events
                </p>
              }
            />
            <FormCheckbox
              key={organiserId}
              control={transactionForm.control}
              name={`organisers.0.hasConsented`}
              className="mt-2"
              labelClassName="text-sm"
              labelContent={
                <p>
                  En cochant cette case, j&apos;accepte de recevoir des offres
                  promotionnelles par courrier électronique de la part de{" "}
                  {organiserName} et que mes données personnelles soient
                  collectées à cette fin.
                </p>
              }
            />
            {structureIds &&
              structureIds.map((structureId, index) => {
                const currentStructure = structuresMapping.get(structureId);

                if (!currentStructure) {
                  return null;
                }

                return (
                  <FormCheckbox
                    key={structureId}
                    control={transactionForm.control}
                    name={`structures.${index}.hasConsented`}
                    className="mt-2"
                    labelClassName="text-sm"
                    labelContent={
                      <p>
                        En cochant cette case, j&apos;accepte de recevoir des
                        offres promotionnelles par courrier électronique de la
                        part de {currentStructure} et que mes données
                        personnelles soient collectées à cette fin.
                      </p>
                    }
                  />
                );
              })}
          </div>
          <TotalCart totalPrice={totalPrice} totalDiscount={totalDiscount} />
          <EmbedNavigation
            setPreviousStep={handlePreviousStep}
            isNextStepSubmit
          />
        </form>
      </Form>
    </div>
  );
}
